import { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import type { OccasionTreeItemType } from '../../schemas/styling';

const OCCASION_IMAGE_SIZE = 56;
const OCCASION_RING_INSET = 3;
const OCCASIONS_LAYOUT_TRANSITION = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.65,
};
const TOP_LEVEL_INTERACTION_ANIMATION = {
  whileHover: { scale: 1.03 },
  whileTap: { scale: 0.97 },
};
const SUB_OCCASION_INTERACTION_ANIMATION = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.98 },
};

type OccasionFilterSelection = {
  occasionId: number | null;
  subOccasionIds: Array<number>;
};

type OccasionFilterProps = {
  occasions: Array<OccasionTreeItemType>;
  selectedOccasionId: number | null;
  selectedSubOccasionIds: Array<number>;
  onChange?: (selection: OccasionFilterSelection) => void;
};

const sortByDisplayOrder = (first: OccasionTreeItemType, second: OccasionTreeItemType) => {
  if (first.display_order !== second.display_order) {
    return first.display_order - second.display_order;
  }

  return first.name.localeCompare(second.name);
};

const isValidId = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const resolveOccasionSelection = (
  occasions: Array<OccasionTreeItemType>,
  selectedOccasionId: number | null,
  selectedSubOccasionIds: Array<number>,
): OccasionFilterSelection => {
  const topLevelOccasions = occasions.filter(occasion => !occasion.parent_id);
  const normalizedSubOccasionIds = selectedSubOccasionIds.filter(isValidId);

  const selectedTopLevelOccasion =
    topLevelOccasions.find(occasion => occasion.id === selectedOccasionId) ?? null;

  if (selectedTopLevelOccasion) {
    const validSubOccasionIds = normalizedSubOccasionIds.filter(subOccasionId =>
      selectedTopLevelOccasion.children.some(child => child.id === subOccasionId),
    );

    return {
      occasionId: selectedTopLevelOccasion.id,
      subOccasionIds: validSubOccasionIds.slice(0, 1),
    };
  }

  for (const topLevelOccasion of topLevelOccasions) {
    const matchedSubOccasion = topLevelOccasion.children.find(child =>
      normalizedSubOccasionIds.includes(child.id),
    );

    if (matchedSubOccasion) {
      return {
        occasionId: topLevelOccasion.id,
        subOccasionIds: [matchedSubOccasion.id],
      };
    }
  }

  return {
    occasionId: null,
    subOccasionIds: [],
  };
};

const OccasionFilter = ({
  occasions,
  selectedOccasionId,
  selectedSubOccasionIds,
  onChange,
}: OccasionFilterProps) => {
  const topLevelScrollRef = useRef<HTMLDivElement | null>(null);
  const subOccasionsScrollRef = useRef<HTMLDivElement | null>(null);

  const resolvedSelection = useMemo(
    () => resolveOccasionSelection(occasions, selectedOccasionId, selectedSubOccasionIds),
    [occasions, selectedOccasionId, selectedSubOccasionIds],
  );

  const topLevelOccasions = useMemo(
    () => occasions.filter(occasion => !occasion.parent_id).sort(sortByDisplayOrder),
    [occasions],
  );

  const selectedSubOccasionId = resolvedSelection.subOccasionIds[0] ?? null;

  const selectedTopLevelOccasion = useMemo(() => {
    if (!resolvedSelection.occasionId) {
      return null;
    }

    return topLevelOccasions.find(occasion => occasion.id === resolvedSelection.occasionId) ?? null;
  }, [topLevelOccasions, resolvedSelection.occasionId]);

  const subOccasions = useMemo(() => {
    return [...(selectedTopLevelOccasion?.children ?? [])].sort(sortByDisplayOrder);
  }, [selectedTopLevelOccasion]);

  useEffect(() => {
    topLevelScrollRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  }, [resolvedSelection.occasionId]);

  useEffect(() => {
    subOccasionsScrollRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  }, [resolvedSelection.occasionId]);

  if (topLevelOccasions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <motion.div
        ref={topLevelScrollRef}
        className="-mx-1 flex gap-3 overflow-x-auto overflow-y-hidden px-1 py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {topLevelOccasions.map(occasion => {
          const imageUrl = occasion.image?.url
            ? updateAttributesOfCdnImage(occasion.image.url, { width: 320, quality: 100 })
            : undefined;
          const isSelected = occasion.id === resolvedSelection.occasionId;

          return (
            <motion.button
              layout
              transition={OCCASIONS_LAYOUT_TRANSITION}
              key={occasion.id}
              type="button"
              className="flex cursor-pointer flex-none shrink-0 appearance-none flex-col items-center gap-2 border-0 bg-transparent p-0"
              onClick={() => {
                if (isSelected) {
                  return;
                }

                const firstSubOccasionId =
                  [...(occasion.children ?? [])].sort(sortByDisplayOrder)[0]?.id ?? null;

                onChange?.({
                  occasionId: occasion.id,
                  subOccasionIds: firstSubOccasionId ? [firstSubOccasionId] : [],
                });
              }}
              whileHover={TOP_LEVEL_INTERACTION_ANIMATION.whileHover}
              whileTap={TOP_LEVEL_INTERACTION_ANIMATION.whileTap}
            >
              <div
                className={clsx(
                  'flex flex-none items-center justify-center rounded-full border bg-muted/30 p-[3px] transition-colors',
                  isSelected ? 'border-foreground shadow-sm' : 'border-transparent',
                )}
                style={{
                  width: `${OCCASION_IMAGE_SIZE + OCCASION_RING_INSET * 2}px`,
                  height: `${OCCASION_IMAGE_SIZE + OCCASION_RING_INSET * 2}px`,
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={occasion.name}
                    className="h-14 w-14 rounded-full object-cover object-center"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background px-2 text-center">
                    <span className="text-[11px] font-medium leading-3 text-foreground">
                      {occasion.name}
                    </span>
                  </div>
                )}
              </div>

              <span
                className={clsx(
                  'w-16 text-center text-[11px] leading-4',
                  isSelected ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {occasion.name}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      <div className="min-h-8">
        {subOccasions.length > 0 ? (
          <motion.div
            ref={subOccasionsScrollRef}
            className="flex h-8 gap-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{ WebkitOverflowScrolling: 'touch' }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.16, ease: [0.215, 0.61, 0.355, 1] }}
          >
            {subOccasions.map(occasion => {
              const isSelected = occasion.id === selectedSubOccasionId;

              return (
                <motion.button
                  key={occasion.id}
                  layout="position"
                  transition={OCCASIONS_LAYOUT_TRANSITION}
                  type="button"
                  className={clsx(
                    'flex h-8 cursor-pointer flex-none items-center whitespace-nowrap rounded-full border px-3 text-sm transition-colors',
                    isSelected
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-background text-muted-foreground',
                  )}
                  onClick={() => {
                    if (isSelected) {
                      return;
                    }

                    onChange?.({
                      occasionId: resolvedSelection.occasionId ?? occasion.parent_id ?? null,
                      subOccasionIds: [occasion.id],
                    });
                  }}
                  whileHover={SUB_OCCASION_INTERACTION_ANIMATION.whileHover}
                  whileTap={SUB_OCCASION_INTERACTION_ANIMATION.whileTap}
                >
                  {occasion.name}
                </motion.button>
              );
            })}
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};

export default OccasionFilter;
